"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLinkUtils = exports.isLinkProps = void 0;
const tslib_1 = require("tslib");
const lodash_1 = tslib_1.__importDefault(require("lodash"));
const links_1 = require("./links");
const linkPatternUtils_1 = require("./linkPatternUtils");
const PAGE_URL_REGEXP = /^\/([^ ?#]*)?[#]?([^ ?#]*)[?]?(.*)/;
const SAME_PAGE_WITH_ANCHOR_REGEXP = /^#([^ ?]*)[?]?(.*)/;
const ABSOLUTE_URL_REGEXP = /^(http|https):\/\/(.*)/;
const DOCUMENT_URL_REGEXP = /^wix:document:\/\/v1\/(.+)\/(.+)/;
const LEGACY_DOCUMENT_URL_REGEXP = /^document:\/\/(.*)/;
const ANCHOR_NAME_TO_TYPE = {
    top: 'SCROLL_TO_TOP',
    bottom: 'SCROLL_TO_BOTTOM',
};
const getPhoneLinkProps = (telUrl) => {
    const [, phoneNumber] = linkPatternUtils_1.PHONE_URL_REGEXP.exec(telUrl);
    return {
        type: 'PhoneLink',
        href: (0, links_1.resolvePhoneLink)({ phoneNumber }),
        target: '_self',
    };
};
const getEscapedQueries = (queryParams) => ({
    subject: encodeURIComponent(queryParams.get('subject') || ''),
    body: encodeURIComponent(queryParams.get('body') || ''),
    bcc: encodeURIComponent(queryParams.get('bcc') || ''),
    cc: encodeURIComponent(queryParams.get('cc') || ''),
});
const getMailtoLinkProps = (mailtoUrl) => {
    const [, recipient, queries] = linkPatternUtils_1.MAILTO_URL_REGEXP.exec(mailtoUrl);
    const escapedQuery = getEscapedQueries(new URLSearchParams(queries));
    return {
        type: 'EmailLink',
        href: (0, links_1.resolveEmailLink)({ recipient, ...escapedQuery }),
        target: '_self',
    };
};
const isDocumentUrl = (url) => DOCUMENT_URL_REGEXP.test(url) || LEGACY_DOCUMENT_URL_REGEXP.test(url);
const isAbsoluteUrl = (url) => ABSOLUTE_URL_REGEXP.test(url);
const isPageUrl = (href) => PAGE_URL_REGEXP.test(href);
const isSamePageAnchorUrl = (href) => SAME_PAGE_WITH_ANCHOR_REGEXP.test(href);
const isDynamicPage = (routersConfig, pageUriSeo) => {
    if (routersConfig) {
        const [prefix] = pageUriSeo.replace('#', '/#').split(/[/]+/);
        const routersWithPrefixFromUrl = Object.values(routersConfig).filter((router) => router.prefix === prefix);
        return routersWithPrefixFromUrl.length === 1;
    }
    return false;
};
const getRouteWithPossibleTpaInnerRoute = (routes, relativePath) => {
    const decodedFullPathPageUriSeo = decodeURIComponent(relativePath);
    const fullPathPageRoute = `./${decodedFullPathPageUriSeo}`;
    if (routes[fullPathPageRoute]) {
        return [decodedFullPathPageUriSeo, ''];
    }
    else {
        const [pageUriSeo, ...tpaInnerRoute] = relativePath.split('/');
        const tpaInnerRoutePath = tpaInnerRoute.length > 0 ? `/${tpaInnerRoute.join('/')}` : '';
        return [pageUriSeo, tpaInnerRoutePath];
    }
};
const getPageRoute = (routingInfo, pageId) => {
    const pageRoute = lodash_1.default.findKey(routingInfo.routes, (route) => {
        if (route.type === 'Dynamic') {
            const pageIds = route.pageIds || [];
            return pageIds.includes(pageId);
        }
        return route.pageId === pageId;
    });
    if (pageRoute) {
        return removeLeadingDotFromRoute(pageRoute);
    }
    throw new Error(`No url route for pageId: ${pageId}`);
};
class UnsupportedLinkTypeError extends Error {
    constructor() {
        super('Unsupported link type');
        this.name = 'UnsupportedLinkTypeError';
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, UnsupportedLinkTypeError);
        }
    }
}
const isLinkProps = (linkProps) => !linkProps.id && !!(linkProps.href || linkProps.linkPopupId || linkProps.anchorDataId || linkProps.anchorCompId);
exports.isLinkProps = isLinkProps;
const removeLeadingDotFromRoute = (url) => url.replace(/^\.\//, '/');
const isScrollTopOrBottomAnchor = (anchorDataId) => ['SCROLL_TO_TOP', 'SCROLL_TO_BOTTOM'].includes(anchorDataId);
const createLinkUtils = ({ routingInfo, metaSiteId, userFileDomainUrl, popupPages, getCompIdByWixCodeNickname, getRoleForCompId, routersConfig, multilingualInfo, isMobileView, isPremiumDomain, }) => {
    const BASE_DOCUMENTS_URL = `https://${metaSiteId}.${userFileDomainUrl}/`;
    const isPopupId = (pageId) => (popupPages ? popupPages[pageId] : false);
    const isDocumentHref = (href) => href.startsWith(BASE_DOCUMENTS_URL) ||
        (isPremiumDomain && href.startsWith((0, links_1.getBaseUrlFilesPath)(routingInfo.externalBaseUrl).href));
    const getDocumentLink = (docId, name = '') => `wix:document://v1/${docId}/${name}`;
    const removeBaseUrlFromHref = (href) => href.replace(routingInfo.externalBaseUrl, '');
    const getDocumentLinkProps = (documentUrl) => {
        const [, docId, name] = DOCUMENT_URL_REGEXP.exec(documentUrl) || LEGACY_DOCUMENT_URL_REGEXP.exec(documentUrl);
        return {
            type: 'DocumentLink',
            href: (0, links_1.resolveDocumentLink)({ docId, name: name || '', indexable: false }, metaSiteId, userFileDomainUrl, routingInfo.externalBaseUrl, isPremiumDomain),
            target: '_blank',
            docInfo: {
                docId,
                name,
            },
        };
    };
    const getExternalLinkProps = (url, target = '_blank', rel = 'noopener') => {
        return {
            type: 'ExternalLink',
            href: url,
            target,
            rel,
        };
    };
    const parsePageUrl = (url) => {
        const [, relativePath = '', anchor = '', queryString = ''] = PAGE_URL_REGEXP.exec(url);
        const relativePageUrlPrefix = relativePath.replace(/\/+$/, '');
        const queryParams = new URLSearchParams(queryString);
        if (!multilingualInfo?.isOriginalLanguage &&
            multilingualInfo?.currentLanguage?.resolutionMethod === 'QueryParam') {
            queryParams.set('lang', multilingualInfo.currentLanguage.languageCode);
        }
        return { relativePageUrlPrefix, anchor, queryString: queryParams.toString() };
    };
    const isDynamicPageUrl = (url) => {
        const { relativePageUrlPrefix } = parsePageUrl(url);
        return isDynamicPage(routersConfig, relativePageUrlPrefix);
    };
    const getHomePageRouteWithPageUriSEO = () => {
        const mainPageRoute = lodash_1.default.findKey(routingInfo.routes, (route) => route.pageId === routingInfo.mainPageId);
        return removeLeadingDotFromRoute(mainPageRoute);
    };
    const getPageLinkProps = (pageUrl, target = '_self', rel) => {
        const { relativePageUrlPrefix = '', anchor = '', queryString } = parsePageUrl(pageUrl);
        const anchorNickname = ANCHOR_NAME_TO_TYPE[anchor] || anchor;
        if (isPopupId(relativePageUrlPrefix)) {
            return {
                type: 'PageLink',
                href: '',
                target: '_self',
                linkPopupId: relativePageUrlPrefix,
            };
        }
        const externalBaseUrl = routingInfo.externalBaseUrl;
        let type;
        let href;
        let isSamePageNavigation;
        if (isDynamicPage(routersConfig, relativePageUrlPrefix)) {
            const relativeHref = `./${relativePageUrlPrefix}`;
            isSamePageNavigation = relativeHref === routingInfo.relativeUrl;
            type = 'DynamicPageLink';
            href = `${externalBaseUrl}/${relativePageUrlPrefix}`;
        }
        else {
            const [pageUriSeoPath, maybeTpaInnerPath] = getRouteWithPossibleTpaInnerRoute(routingInfo.routes, relativePageUrlPrefix);
            const pageUriSeoInCurrentLang = routingInfo.pagesUriSEOs[pageUriSeoPath] || pageUriSeoPath;
            const pageRoute = `./${pageUriSeoInCurrentLang}`;
            const nextRouteConfig = pageRoute === './' ? { pageId: routingInfo.mainPageId } : routingInfo.routes[pageRoute];
            const isHomePageNavigation = nextRouteConfig?.pageId === routingInfo.mainPageId;
            type = 'PageLink';
            href =
                isHomePageNavigation && !maybeTpaInnerPath
                    ? externalBaseUrl
                    : `${externalBaseUrl}/${pageUriSeoInCurrentLang}${maybeTpaInnerPath}`;
            isSamePageNavigation = nextRouteConfig && nextRouteConfig.pageId === routingInfo.pageId;
        }
        const anchorCompId = anchorNickname && getCompIdByWixCodeNickname && getCompIdByWixCodeNickname(anchorNickname);
        const hasAnchorOnSamePage = isSamePageNavigation && anchorCompId;
        const hasAnchorOnOtherPage = anchorNickname && !hasAnchorOnSamePage;
        return {
            href: `${href}${queryString ? `?${new URLSearchParams(queryString).toString()}` : ''}`,
            target,
            rel,
            type,
            // if we have an anchor on the current page, we set the anchor compId
            ...(hasAnchorOnSamePage && { anchorCompId }),
            // if we have an anchor on another page, we set the anchor data item Id
            ...(hasAnchorOnOtherPage && { anchorDataId: anchorNickname }),
        };
    };
    const encodeInnerRoute = (innerRoute) => {
        const [innerRoutePath, stateQueryParams] = innerRoute.split('?');
        if (stateQueryParams) {
            const encodedQueryParams = encodeURIComponent(`?${stateQueryParams}`);
            return innerRoutePath ? `${innerRoutePath}${encodedQueryParams}` : encodedQueryParams;
        }
        return innerRoutePath;
    };
    const linkTypeToUrlResolverFn = {
        AnchorLink: (linkData) => {
            const { anchorDataId, pageId } = linkData;
            const isScrollTopOrBottom = isScrollTopOrBottomAnchor(anchorDataId);
            const nextPageId = isScrollTopOrBottom ? routingInfo.pageId : pageId.replace(/^#/, '');
            const nextAnchorDataId = anchorDataId.startsWith('#') ? anchorDataId : `#${anchorDataId}`;
            // get page route for current page id for top/bottom anchors, otherwise get route for pageId from data item
            const pageRoute = getPageRoute(routingInfo, nextPageId);
            return `${pageRoute}${nextAnchorDataId}`;
        },
        DocumentLink: (linkData) => {
            const { docId, name } = linkData;
            return getDocumentLink(docId, name);
        },
        ExternalLink: (linkData) => {
            const { url } = linkData;
            return url;
        },
        DynamicPageLink: (linkData) => {
            const { routerId, innerRoute, anchorDataId = '' } = linkData;
            const prefix = `/${routersConfig[routerId].prefix}`;
            // query params passed in the innerRoute should be encoded as a path to properly apply them to the section after navigation
            const encodedInnerRoute = innerRoute ? encodeInnerRoute(innerRoute) : innerRoute;
            const suffix = encodedInnerRoute ? `/${encodedInnerRoute}${anchorDataId}` : anchorDataId;
            return `${prefix}${suffix}`;
        },
        TpaPageLink: (linkData) => {
            const { pageId, path = '' } = linkData;
            const _pageId = pageId.replace(/^#/, '');
            const prefix = routingInfo.pageIdToPrefix[_pageId];
            const pageUriSeo = routingInfo.pages[_pageId].pageUriSEO;
            const relativeUrl = prefix ? `/${prefix}/${pageUriSeo}` : `/${pageUriSeo}`;
            const encodedPath = encodeInnerRoute(path);
            const isPathWithPageUriSEO = path.startsWith(relativeUrl);
            if (isPathWithPageUriSEO) {
                return encodedPath;
            }
            const suffix = encodedPath ? `/${encodedPath}` : '';
            return `${relativeUrl}${suffix}`;
        },
        PageLink: (linkData) => {
            const { pageId: pageIdOrData } = linkData;
            const pageId = ((typeof pageIdOrData === 'string' ? pageIdOrData : pageIdOrData.id) || '').replace(/^#/, '');
            if (isPopupId(pageId)) {
                return `/${pageId}`;
            }
            if (pageId === routingInfo.mainPageId) {
                return '/';
            }
            return getPageRoute(routingInfo, pageId);
        },
        PhoneLink: (linkData) => (0, links_1.resolvePhoneLink)(linkData),
        EmailLink: (linkData) => (0, links_1.resolveEmailLink)(linkData),
    };
    return {
        isAbsoluteUrl,
        isDynamicPage: isDynamicPageUrl,
        getImpliedLink: (text) => (0, links_1.getImpliedLink)(text, isMobileView),
        getImpliedLinks: (text, config) => (0, links_1.getImpliedLinks)(text, isMobileView, config),
        getLink: ({ href: linkHref = '', linkPopupId, anchorCompId = '', anchorDataId: _anchorDataId = '', docInfo, type, } = {}) => {
            if (linkPopupId) {
                return `/${linkPopupId}`;
            }
            if ((0, linkPatternUtils_1.isMailtoUrl)(linkHref)) {
                return linkHref;
            }
            if (isDocumentHref(linkHref)) {
                return getDocumentLink(docInfo.docId, docInfo.name);
            }
            const anchorDataId = typeof _anchorDataId === 'string' ? _anchorDataId : _anchorDataId?.id;
            if (isScrollTopOrBottomAnchor(anchorDataId)) {
                return `#${lodash_1.default.invert(ANCHOR_NAME_TO_TYPE)[anchorDataId]}`;
            }
            const isExternalLink = type === 'ExternalLink';
            if (isExternalLink) {
                return linkHref;
            }
            // remove query params if exist
            const [href] = linkHref.split('?');
            const anchor = getRoleForCompId?.(anchorCompId, 'wixCode') || anchorDataId;
            const anchorId = anchor ? `#${anchor}` : '';
            const isHomePageUrl = href === routingInfo.externalBaseUrl;
            const link = isHomePageUrl ? getHomePageRouteWithPageUriSEO() : removeBaseUrlFromHref(href);
            return `${link}${anchorId}`;
        },
        getLinkProps: (url, target, rel) => {
            if (isSamePageAnchorUrl(url)) {
                const relativeUrl = removeLeadingDotFromRoute(routingInfo.relativeUrl);
                const currentPageUriSEOWithAnchorUrl = `${relativeUrl}${url}`;
                return getPageLinkProps(currentPageUriSEOWithAnchorUrl, target);
            }
            if (isPageUrl(url)) {
                return getPageLinkProps(url, target, rel);
            }
            if ((0, linkPatternUtils_1.isMailtoUrl)(url)) {
                return getMailtoLinkProps(url);
            }
            if ((0, linkPatternUtils_1.isPhoneUrl)(url)) {
                return getPhoneLinkProps(url);
            }
            if (isAbsoluteUrl(url)) {
                return getExternalLinkProps(url, target, rel);
            }
            if (isDocumentUrl(url)) {
                return getDocumentLinkProps(url);
            }
            throw new UnsupportedLinkTypeError();
        },
        getLinkUrlFromDataItem: (linkData) => {
            const linkUrlResolverFn = linkTypeToUrlResolverFn[linkData.type];
            if (linkUrlResolverFn) {
                return linkUrlResolverFn(linkData);
            }
            throw new Error('Provided link type is not supported');
        },
    };
};
exports.createLinkUtils = createLinkUtils;
//# sourceMappingURL=oldLinkUtils.js.map